<template>
  <div class="ba-root" ref="root" :style="{ height: (STAGE_H * scale) + 'px' }">
    <div class="ba-stage" :style="{ transform: 'translateX(' + offsetX + 'px) scale(' + scale + ')' }">
      <!-- Broadcast side view of the court: one flat SVG where every point
           (floor, hoops, ball) goes through the same projection (p3). -->
      <svg class="ba-scene" :viewBox="'0 0 ' + STAGE_W + ' ' + STAGE_H" preserveAspectRatio="none">
        <defs>
          <pattern v-if="crowd" :id="uid + 'crowd'" patternUnits="userSpaceOnUse" width="90" height="45">
            <image :href="crowd" x="0" y="0" width="90" height="45" />
          </pattern>
          <linearGradient :id="uid + 'floor'" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stop-color="#d9a066" />
            <stop offset="1" stop-color="#b97a42" />
          </linearGradient>
          <linearGradient :id="uid + 'surround'" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stop-color="#2e3b57" />
            <stop offset="1" stop-color="#222c44" />
          </linearGradient>
          <radialGradient :id="uid + 'ball'" cx="0.35" cy="0.35" r="0.7">
            <stop offset="0" stop-color="#ffa149" />
            <stop offset="1" stop-color="#c2530f" />
          </radialGradient>
          <radialGradient :id="uid + 'glow'" cx="0.5" cy="0.5" r="0.5">
            <stop offset="0" stop-color="#fff" stop-opacity="0.9" />
            <stop offset="1" stop-color="#fff" stop-opacity="0" />
          </radialGradient>
        </defs>

        <!-- Stands. -->
        <rect x="0" y="0" :width="STAGE_W" :height="STAGE_H" fill="#15151c" />
        <rect v-if="crowd" x="0" y="0" :width="STAGE_W" :height="STAGE_H" :fill="'url(#' + uid + 'crowd)'" opacity="0.8" />

        <!-- Floor around the court, then the dark foreground band. -->
        <polygon :points="surround" :fill="'url(#' + uid + 'surround)'" />
        <rect x="0" :y="FRONT_Y" :width="STAGE_W" :height="STAGE_H - FRONT_Y" fill="#1a2236" />
        <line x1="0" :y1="FRONT_Y" :x2="STAGE_W" :y2="FRONT_Y" stroke="rgba(255,255,255,0.3)" stroke-width="1" />

        <ScrollingBoards :boards="boards" :uid="uid" :logo-url="logoUrl" :board-text="boardText" fill="#111827" top-fill="#1f2a40" />

        <!-- Parquet, with the planks running along the court. -->
        <polygon :points="courtPoly" :fill="'url(#' + uid + 'floor)'" />
        <g stroke="rgba(90,45,10,0.18)" stroke-width="0.6">
          <path v-for="(d, i) in planks" :key="'pl' + i" :d="d" />
        </g>
        <!-- Painted keys, tinted with each team's colour. -->
        <polygon :points="keys.left" :fill="homeTint" />
        <polygon :points="keys.right" :fill="awayTint" />
        <!-- Possession: the attacked half lights up. -->
        <polygon v-if="attackGlow" :points="attackGlow.points" :fill="attackGlow.color" class="ba-attack-glow" />

        <!-- Logo painted on the parquet inside the near sideline, at halfway. -->
        <image v-if="showSirplayLogo" :href="PITCH_LOGO" :transform="logoDecal" x="-180" y="-50" width="360" height="100" preserveAspectRatio="none" class="arena-logo" />

        <!-- Court lines. -->
        <g fill="none" stroke="rgba(255,255,255,0.92)" stroke-width="1.5" stroke-linejoin="round">
          <path v-for="(d, i) in courtLines" :key="'l' + i" :d="d" />
        </g>

        <!-- Hoops: stanchion, backboard, rim and net. The far-side rim
             half is drawn before the ball and the near half after, so a
             made shot drops through the ring. -->
        <g v-for="hp in hoops" :key="'hoop' + hp.side">
          <path :d="hp.pole" stroke="#1f2937" stroke-width="3.2" fill="none" stroke-linecap="round" />
          <polygon :points="hp.padBase" fill="#1f2937" />
          <polygon :points="hp.board" fill="rgba(230,240,255,0.55)" stroke="#fff" stroke-width="1" />
          <polygon :points="hp.square" fill="none" stroke="#e11d48" stroke-width="0.9" />
          <path :d="hp.rimBack" stroke="#ea580c" stroke-width="1.8" fill="none" />
        </g>

        <!-- Ball: shadow on the floor + the ball itself. -->
        <ellipse :cx="ballView.shadow.x" :cy="ballView.shadow.y" :rx="5 * ballView.k" :ry="1.8 * ballView.k" fill="rgba(0,0,0,0.35)" />
        <g :transform="'translate(' + ballView.x.toFixed(1) + ',' + ballView.y.toFixed(1) + ')'">
          <circle r="5" :transform="'scale(' + ballView.k.toFixed(3) + ')'" :fill="'url(#' + uid + 'ball)'" />
          <g :transform="'scale(' + ballView.k.toFixed(3) + ') rotate(' + ballView.spin.toFixed(0) + ')'" stroke="rgba(40,15,0,0.75)" stroke-width="0.7" fill="none">
            <line x1="-5" y1="0" x2="5" y2="0" />
            <line x1="0" y1="-5" x2="0" y2="5" />
            <path d="M-3.5,-3.5 Q0,0 -3.5,3.5 M3.5,-3.5 Q0,0 3.5,3.5" />
          </g>
        </g>

        <g v-for="hp in hoops" :key="'rimf' + hp.side">
          <path :d="hp.net" :class="{ 'ba-net-swish': swish === hp.side }" stroke="rgba(255,255,255,0.85)" stroke-width="0.7" fill="none" />
          <path :d="hp.rimFront" stroke="#f97316" stroke-width="2" fill="none" />
          <circle v-if="swish === hp.side" :key="'flash' + swishKey" :cx="hp.rimCenter.x" :cy="hp.rimCenter.y" r="16" :fill="'url(#' + uid + 'glow)'" class="ba-rim-flash" />
        </g>
      </svg>

      <!-- Scoreboard. -->
      <div class="ba-score">
        <span class="ba-score-team is-home" :style="{ background: homeCol }">{{ homeName }}</span>
        <span class="ba-score-pts" :key="'h' + popKey" :class="{ 'is-pop': popSide === '1' }">{{ curH }}</span>
        <span class="ba-score-clock"><b>{{ periodText }}</b>{{ time }}</span>
        <span class="ba-score-pts" :key="'a' + popKey" :class="{ 'is-pop': popSide === '2' }">{{ curA }}</span>
        <span class="ba-score-team is-away" :style="{ background: awayCol }">{{ awayName }}</span>
      </div>
      <div v-if="(possessionSide === '1' || possessionSide === '2') && !finished" class="ba-possession">
        <span class="ba-possession-label">{{ $t('statistics.possession') }}</span>
        <span class="ba-possession-name">{{ possessionSide === '1' ? homeName : awayName }}</span>
        <span class="ba-possession-bar" :style="{ background: possessionSide === '1' ? homeCol : awayCol }"></span>
      </div>

      <!-- Basket scored: full-stage overlay, then the scoreboard updates. -->
      <transition name="ba-overlay">
        <div v-if="overlay" :key="overlay.key" class="ba-point-overlay" :style="{ '--side-color': overlay.color }">
          <div class="ba-point-value">+{{ overlay.points }}</div>
          <div class="ba-point-title">{{ overlay.title }}</div>
          <div class="ba-point-team">{{ overlay.name }}</div>
          <div class="ba-point-score">
            <span :class="{ 'is-won': overlay.side === '1' }">{{ overlay.h }}</span>
            <span class="ba-point-sep">-</span>
            <span :class="{ 'is-won': overlay.side === '2' }">{{ overlay.a }}</span>
          </div>
        </div>
      </transition>

      <!-- Timeout: badge over the dimmed court for a few seconds. -->
      <transition name="ba-card">
        <div v-if="cardShown" :key="cardShown.key" class="ba-card-veil">
          <div class="ba-card" :style="{ '--side-color': cardShown.color }">
            <svg class="ba-card-icon" viewBox="0 0 40 40">
              <rect x="16" y="2" width="8" height="4" rx="1" fill="#e5e7eb" />
              <line x1="20" y1="6" x2="20" y2="9" stroke="#e5e7eb" stroke-width="2.4" />
              <circle cx="20" cy="23" r="14" fill="none" stroke="#e5e7eb" stroke-width="2.6" />
              <path d="M20,23 L20,13 A10,10 0 0 1 29.5,20 Z" :fill="cardShown.color" />
              <line x1="20" y1="23" x2="20" y2="13" stroke="#fff" stroke-width="2" stroke-linecap="round" class="ba-card-hand" />
            </svg>
            <div class="ba-card-title">{{ cardShown.title }}</div>
            <div v-if="cardShown.team" class="ba-card-team">{{ cardShown.team }}</div>
          </div>
        </div>
      </transition>

      <!-- Match over: big badge that stays up. -->
      <transition name="ba-final">
        <div v-if="finished" class="ba-final">
          <div class="ba-final-badge">
            <svg class="ba-final-icon" viewBox="0 0 40 40">
              <path d="M12,5 H28 V15 a8,8 0 0 1 -16,0 Z" fill="#facc15" />
              <path d="M12,8 H6 a6,6 0 0 0 6,9 M28,8 H34 a6,6 0 0 1 -6,9" fill="none" stroke="#facc15" stroke-width="2.4" />
              <rect x="18" y="22" width="4" height="7" fill="#eab308" />
              <rect x="12" y="29" width="16" height="5" rx="1.5" fill="#eab308" />
            </svg>
            <div class="ba-final-title">{{ $t('statistics.matchfinished') }}</div>
            <div class="ba-final-score">
              <span :class="{ 'is-win': +scoreH > +scoreA }">{{ homeName }} {{ scoreH }}</span>
              <span class="ba-point-sep">-</span>
              <span :class="{ 'is-win': +scoreA > +scoreH }">{{ scoreA }} {{ awayName }}</span>
            </div>
            <div v-if="periods.length" class="ba-final-sets">
              <span v-for="(q, i) in periods" :key="'q' + i">{{ q.h }}-{{ q.a }}</span>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import ScrollingBoards from '@/components/arenamatchtracker/ScrollingBoards.vue';
import { makeProjection, pt, seg, crowdTexture, makeBoard, stageScaleMixin, floorLogo, PITCH_LOGO, sirplayLicenceMixin } from '@/services/matchtracker/broadcastScene';

// Stage = fixed broadcast frame, scaled to the container width (see stageScaleMixin).
const ZOOM = 1.1;
const STAGE_W = 460;
const STAGE_H = 270;
const FRONT_Y = 214;
// Court projection (see makeProjection).
const { proj } = makeProjection({ farY: 60, nearY: 196, farW: 292, nearW: 404, farCx: 229, nearCx: 230 });
// Court in metres (FIBA): 28 x 15. X along the length (0 = left
// baseline), Y across (0 = far sideline), H height above the floor.
const CL = 28;
const CW = 15;
const HOOP_X = 1.575;
const RIM_H = 3.05;
const RIM_R = 0.23;
const THREE_R = 6.75;
const CORNER_Y = 0.9;
const KEY_W = 4.9;
const KEY_L = 5.8;
const FT_R = 1.8;
// Metres -> px scale of a height, relative to the local court width.
const H_SCALE = 0.85;
// Run-off / ad-board corners in screen px.
const FAR_L = [86, 42];
const FAR_R = [374, 42];
const SIDE_L = [0, 112];
const SIDE_R = [STAGE_W, 106];

// Shot timings (ms) and how long the overlay stays up.
const T_DRIVE = 1100;
const T_SHOT = { 1: 850, 2: 800, 3: 1050 };
const T_DROP = 380;
const T_SETTLE = 450;
const OVERLAY_MS = 2600;
const TIMEOUT_TYPE = 31;
const CARD_MS = 5000;

const rand = (a, b) => a + Math.random() * (b - a);
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

// 3D point on the court (metres) -> screen.
function p3(X, Y, H = 0) {
  const g = proj(X / CL, Y / CW);
  const k = g.w / CL;
  return { x: g.x, y: g.y - H * k * H_SCALE, k: g.w / 292 };
}
const poly = list => list.map(pt).join(' ');
const pathOf = list => 'M' + list.map(pt).join(' L');
// Hoop of each side: home attacks the right one, away the left one.
const hoopX = side => (side === 'L' ? HOOP_X : CL - HOOP_X);
const attackHoop = team => (team === '1' ? 'R' : 'L');

export default {
  name: 'BasketArena',
  components: { ScrollingBoards },
  mixins: [stageScaleMixin, sirplayLicenceMixin],
  props: {
    homeName: { type: String, default: '' },
    awayName: { type: String, default: '' },
    scoreH: { type: [String, Number], default: 0 },
    scoreA: { type: [String, Number], default: 0 },
    // '1' home / '2' away / null.
    possessionSide: { type: String, default: null },
    time: { type: String, default: '' },
    periodText: { type: String, default: '' },
    // Points per quarter so far, [{ h, a }] — shown on the final badge.
    periods: { type: Array, default: () => [] },
    // Incident currently shown by MatchTracker (31 = timeout) and its side.
    actionType: { type: Number, default: 0 },
    actionSide: { type: String, default: null },
    actionKey: { type: [String, Number], default: '' },
    finished: { type: Boolean, default: false },
    homeColor: { type: String, default: '' },
    awayColor: { type: String, default: '' },
    logoUrl: { type: String, default: '' },
    boardText: { type: String, default: 'LIVE' }
  },
  data() {
    return {
      PITCH_LOGO,
      STAGE_W,
      STAGE_H,
      FRONT_Y,
      ZOOM,
      crowd: '',
      // Score shown on the scoreboard: lags the props while a basket is animated.
      curH: parseInt(this.scoreH) || 0,
      curA: parseInt(this.scoreA) || 0,
      lastH: parseInt(this.scoreH) || 0,
      lastA: parseInt(this.scoreA) || 0,
      ball: { X: CL / 2, Y: CW / 2, H: 0, spin: 0 },
      shot: null,
      raf: null,
      overlay: null,
      overlayTimer: null,
      popSide: null,
      cardShown: null,
      cardTimer: null,
      popKey: 0,
      swish: null,
      swishKey: 0,
      // Idle dribble: wandering target in the attacked half.
      wander: { from: { X: CL / 2, Y: CW / 2 }, to: { X: CL / 2, Y: CW / 2 }, t0: 0, dur: 1 }
    };
  },
  watch: {
    actionKey() {
      if (this.finished || !this.actionKey) return;
      const side = this.actionSide === '1' || this.actionSide === '2' ? this.actionSide : null;
      if (this.actionType === TIMEOUT_TYPE) {
        clearTimeout(this.cardTimer);
        this.cardShown = {
          key: this.actionKey,
          title: this.$t('statistics.timeouts'),
          team: side ? (side === '1' ? this.homeName : this.awayName) : '',
          color: side === '1' ? this.homeCol : (side === '2' ? this.awayCol : '#94a3b8')
        };
        this.cardTimer = setTimeout(() => { this.cardShown = null; }, CARD_MS);
      }
    },
    scoreKey() {
      const h = parseInt(this.scoreH) || 0;
      const a = parseInt(this.scoreA) || 0;
      const dh = h - this.lastH;
      const da = a - this.lastA;
      this.lastH = h;
      this.lastA = a;
      // A single basket from one team animates; anything else (score
      // corrections, several baskets in one update) just updates.
      const side = dh > 0 && da === 0 ? '1' : (da > 0 && dh === 0 ? '2' : null);
      const points = side === '1' ? dh : da;
      // An earlier basket still animating is settled first.
      this.finishShot();
      if (!side || points > 3 || this.finished) {
        this.curH = h;
        this.curA = a;
        return;
      }
      this.curH = h - (side === '1' ? points : 0);
      this.curA = a - (side === '2' ? points : 0);
      this.startShot(side, points);
    },
    finished(val) {
      if (val) {
        this.finishShot();
        this.curH = parseInt(this.scoreH) || 0;
        this.curA = parseInt(this.scoreA) || 0;
      }
    }
  },
  mounted() {
    this.crowd = crowdTexture('arena');
    this.raf = requestAnimationFrame(this.tick);
  },
  beforeDestroy() {
    cancelAnimationFrame(this.raf);
    clearTimeout(this.overlayTimer);
    clearTimeout(this.cardTimer);
  },
  methods: {
    // Team colour as a translucent paint.
    tint(hex, alpha) {
      const m = /^#([0-9a-f]{6})$/i.exec(hex || '');
      if (!m) return `rgba(45,95,174,${alpha})`;
      const n = parseInt(m[1], 16);
      return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
    },
    // One animation loop for everything that moves: the idle dribble and
    // the scoring play.
    tick(ts) {
      if (this.shot) this.stepShot(ts);
      else if (this.cardShown) this.ball = { X: CL / 2, Y: CW / 2, H: 0, spin: this.ball.spin };
      else if (!this.finished) this.stepDribble(ts);
      this.raf = requestAnimationFrame(this.tick);
    },
    // Idle: the team in possession dribbles around its attacking half.
    stepDribble(ts) {
      const w = this.wander;
      let t = (ts - w.t0) / w.dur;
      if (t >= 1 || w.t0 === 0) {
        const team = this.possessionSide;
        const from = { X: this.ball.X, Y: this.ball.Y };
        let to;
        if (team === '1' || team === '2') {
          const hx = hoopX(attackHoop(team));
          const dir = team === '1' ? -1 : 1;
          to = { X: hx + dir * rand(2.5, 9), Y: rand(2.5, CW - 2.5) };
        } else {
          to = { X: CL / 2 + rand(-1.2, 1.2), Y: CW / 2 + rand(-1.2, 1.2) };
        }
        this.wander = { from, to, t0: ts, dur: rand(1400, 2200) };
        t = 0;
      }
      const e = t * t * (3 - 2 * t);
      const X = w.from.X + (w.to.X - w.from.X) * e;
      const Y = w.from.Y + (w.to.Y - w.from.Y) * e;
      // Dribble: bounces between the floor and the hand (~1m), ~2.5 a second.
      const phase = (ts / 400) % 1;
      const H = 0.95 * Math.abs(Math.sin(phase * Math.PI));
      this.ball = { X, Y, H, spin: this.ball.spin + 3 };
    },
    // Builds the scoring play: drive to the shooting spot, shot, drop
    // through the net, a bounce under the hoop.
    startShot(side, points) {
      const hs = attackHoop(side);
      const hx = hoopX(hs);
      const dir = hs === 'R' ? -1 : 1;
      let spot;
      if (points === 1) {
        spot = { X: hx + dir * (KEY_L - HOOP_X), Y: CW / 2 };
      } else if (points === 3) {
        const ang = rand(-1.25, 1.25);
        spot = { X: hx + dir * Math.cos(ang) * (THREE_R + 0.6), Y: clamp(CW / 2 + Math.sin(ang) * (THREE_R + 0.6), 0.6, CW - 0.6) };
      } else {
        const ang = rand(-1.1, 1.1);
        const r = rand(1.2, 5);
        spot = { X: hx + dir * Math.cos(ang) * r, Y: CW / 2 + Math.sin(ang) * r };
      }
      const start = { X: this.ball.X, Y: this.ball.Y, H: this.ball.H };
      const segs = [];
      // Free throws start from the line; the rest drive there dribbling.
      if (points === 1) {
        segs.push({ from: { ...spot, H: 0 }, to: { ...spot, H: 0 }, dur: 900, dribble: true });
      } else {
        segs.push({ from: start, to: { ...spot, H: 0 }, dur: T_DRIVE, dribble: true });
      }
      const rimTop = { X: hx, Y: CW / 2, H: RIM_H + 0.25 };
      segs.push({ from: { ...spot, H: 2.2 }, to: rimTop, dur: T_SHOT[points], peak: points === 3 ? 3.2 : (points === 1 ? 2.2 : 1.7), swish: hs });
      segs.push({ from: rimTop, to: { X: hx, Y: CW / 2, H: 0.1 }, dur: T_DROP });
      segs.push({ from: { X: hx, Y: CW / 2, H: 0.1 }, to: { X: hx + dir * 1.2, Y: CW / 2 + rand(-1, 1), H: 0 }, dur: T_SETTLE, peak: 0.6 });
      this.shot = { side, points, segs, i: 0, t0: null };
    },
    stepShot(ts) {
      const sh = this.shot;
      if (sh.t0 === null) sh.t0 = ts;
      let sg = sh.segs[sh.i];
      let t = (ts - sh.t0) / sg.dur;
      while (t >= 1) {
        if (sg.swish) {
          this.swish = sg.swish;
          this.swishKey++;
        }
        sh.i++;
        if (sh.i >= sh.segs.length) {
          this.finishShot();
          return;
        }
        sh.t0 += sg.dur;
        sg = sh.segs[sh.i];
        t = (ts - sh.t0) / sg.dur;
      }
      const e = sg.dribble ? t * t * (3 - 2 * t) : t;
      const X = sg.from.X + (sg.to.X - sg.from.X) * e;
      const Y = sg.from.Y + (sg.to.Y - sg.from.Y) * e;
      let H;
      if (sg.dribble) H = 0.95 * Math.abs(Math.sin(((ts / 330) % 1) * Math.PI));
      else H = sg.from.H + (sg.to.H - sg.from.H) * t + 4 * (sg.peak || 0) * t * (1 - t);
      this.ball = { X, Y, H, spin: this.ball.spin + (sg.dribble ? 3 : 9) };
    },
    // Ends the current scoring play (also when a newer one interrupts it):
    // scoreboard moves on and the overlay comes up.
    finishShot() {
      const sh = this.shot;
      if (!sh) return;
      this.shot = null;
      if (sh.side === '1') this.curH += sh.points;
      else this.curA += sh.points;
      this.wander = { ...this.wander, t0: 0 };
      this.popKey++;
      this.popSide = sh.side;
      const title = sh.points === 3 ? 'statistics.triple' : (sh.points === 1 ? 'statistics.freethrows' : 'statistics.basket');
      this.overlay = {
        key: this.popKey,
        side: sh.side,
        points: sh.points,
        title: this.$t(title),
        name: sh.side === '1' ? this.homeName : this.awayName,
        color: sh.side === '1' ? this.homeCol : this.awayCol,
        h: this.curH,
        a: this.curA
      };
      clearTimeout(this.overlayTimer);
      this.overlayTimer = setTimeout(() => { this.overlay = null; this.swish = null; }, OVERLAY_MS);
    }
  },
  computed: {
    logoDecal() {
      return floorLogo(p3, CL / 2, CW - 1.5, 0.8);
    },
    // SVG ids are document-global: keep each instance's gradients apart.
    uid() { return 'ba' + this._uid + '-'; },
    scoreKey() { return this.scoreH + '|' + this.scoreA; },
    homeCol() { return this.homeColor || '#2d5fae'; },
    awayCol() { return this.awayColor || '#c0392b'; },
    homeTint() { return this.tint(this.homeCol, 0.55); },
    awayTint() { return this.tint(this.awayCol, 0.55); },
    surround() {
      return [FAR_L, FAR_R, SIDE_R, [STAGE_W, FRONT_Y + 10], [0, FRONT_Y + 10], SIDE_L].map(p => p.join(',')).join(' ');
    },
    boards() {
      const P = p => ({ x: p[0], y: p[1] });
      return [
        makeBoard('far', P(FAR_L), P(FAR_R), 15, 15, 5, 40),
        makeBoard('left', P(SIDE_L), P(FAR_L), 24, 15, 2, 38),
        makeBoard('right', P(FAR_R), P(SIDE_R), 15, 24, 2, 38)
      ];
    },
    courtPoly() {
      const m = 0.9;
      return poly([p3(-m, -m), p3(CL + m, -m), p3(CL + m, CW + m), p3(-m, CW + m)]);
    },
    planks() {
      const list = [];
      for (let y = 0.6; y < CW; y += 0.6) list.push(seg(p3(-0.9, y), p3(CL + 0.9, y)));
      return list;
    },
    keys() {
      const k = x0 => {
        const x1 = x0 === 0 ? KEY_L : CL - KEY_L;
        const y0 = (CW - KEY_W) / 2;
        return poly([p3(x0, y0), p3(x1, y0), p3(x1, y0 + KEY_W), p3(x0, y0 + KEY_W)]);
      };
      return { left: k(0), right: k(CL) };
    },
    attackGlow() {
      const team = this.possessionSide;
      if ((team !== '1' && team !== '2') || this.finished) return null;
      const x0 = team === '1' ? CL / 2 : 0;
      const x1 = team === '1' ? CL : CL / 2;
      return {
        points: poly([p3(x0, 0), p3(x1, 0), p3(x1, CW), p3(x0, CW)]),
        color: this.tint(team === '1' ? this.homeCol : this.awayCol, 0.16)
      };
    },
    courtLines() {
      const L = [];
      L.push(pathOf([p3(0, 0), p3(CL, 0), p3(CL, CW), p3(0, CW)]) + ' Z');
      L.push(seg(p3(CL / 2, 0), p3(CL / 2, CW)));
      const circle = (cx, cy, r, a0 = 0, a1 = Math.PI * 2, n = 40) => {
        const out = [];
        for (let i = 0; i <= n; i++) {
          const a = a0 + (a1 - a0) * i / n;
          out.push(p3(cx + Math.cos(a) * r, cy + Math.sin(a) * r));
        }
        return pathOf(out);
      };
      L.push(circle(CL / 2, CW / 2, FT_R));
      [0, CL].forEach(x0 => {
        const dir = x0 === 0 ? 1 : -1;
        const hx = x0 + dir * HOOP_X;
        const y0 = (CW - KEY_W) / 2;
        const ftX = x0 + dir * KEY_L;
        // Key outline and free-throw circle.
        L.push(pathOf([p3(x0, y0), p3(ftX, y0), p3(ftX, y0 + KEY_W), p3(x0, y0 + KEY_W)]));
        L.push(circle(ftX, CW / 2, FT_R));
        // Three-point line: corner straights + arc around the hoop.
        const dy = CW / 2 - CORNER_Y;
        const cornerX = Math.sqrt(THREE_R * THREE_R - dy * dy);
        L.push(seg(p3(x0, CORNER_Y), p3(hx + dir * cornerX, CORNER_Y)));
        L.push(seg(p3(x0, CW - CORNER_Y), p3(hx + dir * cornerX, CW - CORNER_Y)));
        const aC = Math.asin(dy / THREE_R);
        const arcA0 = dir === 1 ? -aC : Math.PI + aC;
        const arcA1 = dir === 1 ? aC : Math.PI - aC;
        L.push(circle(hx, CW / 2, THREE_R, arcA0, arcA1, 50));
        // No-charge semicircle.
        L.push(circle(hx, CW / 2, 1.25, dir === 1 ? -Math.PI / 2 : Math.PI / 2, dir === 1 ? Math.PI / 2 : Math.PI * 1.5, 20));
      });
      return L;
    },
    hoops() {
      return ['L', 'R'].map(side => {
        const dir = side === 'L' ? 1 : -1;
        const x0 = side === 'L' ? 0 : CL;
        const hx = hoopX(side);
        const bx = x0 + dir * 1.2;
        const poleX = x0 - dir * 1.1;
        const cy = CW / 2;
        const ring = (h, r, a0, a1, n = 16) => {
          const out = [];
          for (let i = 0; i <= n; i++) {
            const a = a0 + (a1 - a0) * i / n;
            out.push(p3(hx + Math.cos(a) * r, cy + Math.sin(a) * r, h));
          }
          return out;
        };
        // Net: strings from the rim down to a narrower ring.
        const top = ring(RIM_H, RIM_R, 0, Math.PI * 2, 12);
        const bottom = ring(RIM_H - 0.42, RIM_R * 0.6, 0, Math.PI * 2, 12);
        let net = '';
        for (let i = 0; i < 12; i++) net += seg(top[i], bottom[(i + 1) % 12]) + ' ' + seg(top[i], bottom[(i + 11) % 12]) + ' ';
        net += pathOf(bottom);
        return {
          side,
          pole: pathOf([p3(poleX, cy, 0), p3(poleX, cy, 3.35), p3(bx, cy, 3.35)]),
          padBase: poly([p3(poleX - 0.35, cy - 0.5), p3(poleX + 0.35, cy - 0.5), p3(poleX + 0.35, cy + 0.5), p3(poleX - 0.35, cy + 0.5)]),
          board: poly([p3(bx, cy - 0.9, 2.9), p3(bx, cy + 0.9, 2.9), p3(bx, cy + 0.9, 3.95), p3(bx, cy - 0.9, 3.95)]),
          square: poly([p3(bx, cy - 0.3, 3.05), p3(bx, cy + 0.3, 3.05), p3(bx, cy + 0.3, 3.5), p3(bx, cy - 0.3, 3.5)]),
          // Far half of the ring (Y < centre) behind the ball, near half in front.
          rimBack: pathOf(ring(RIM_H, RIM_R, Math.PI, Math.PI * 2)),
          rimFront: pathOf(ring(RIM_H, RIM_R, 0, Math.PI)),
          net,
          rimCenter: p3(hx, cy, RIM_H)
        };
      });
    },
    ballView() {
      const b = this.ball;
      const g = p3(b.X, b.Y, 0);
      const up = p3(b.X, b.Y, b.H + 0.12);
      return { x: up.x, y: up.y - 4 * g.k, k: g.k, shadow: { x: g.x + 1, y: g.y + 1 }, spin: b.spin };
    }
  }
};
</script>

<style scoped>
.ba-root { position: relative; width: 100%; overflow: hidden; background: #15151c; }
.ba-stage { position: absolute; top: 0; left: 0; width: 460px; height: 270px; transform-origin: 0 0; font-family: Roboto, Arial, sans-serif; }
.ba-scene { position: absolute; inset: 0; width: 100%; height: 100%; display: block; }

.ba-attack-glow { animation: ba-glow 2.4s ease-in-out infinite; }
@keyframes ba-glow { 0%, 100% { opacity: 0.6; } 50% { opacity: 1; } }
.ba-net-swish { transform-box: fill-box; transform-origin: 50% 0%; animation: ba-swish 0.6s ease-out; }
@keyframes ba-swish { 0% { transform: scaleY(1); } 30% { transform: scaleY(1.45) scaleX(0.85); } 100% { transform: scaleY(1); } }
.ba-rim-flash { transform-box: fill-box; transform-origin: 50% 50%; animation: ba-flash 0.8s ease-out forwards; }
@keyframes ba-flash { from { transform: scale(0.3); opacity: 1; } to { transform: scale(1.8); opacity: 0; } }

/* Scoreboard. */
.ba-score { position: absolute; top: 7px; left: 50%; transform: translateX(-50%); display: flex; align-items: stretch; height: 24px; z-index: 2; box-shadow: 0 2px 6px rgba(0, 0, 0, 0.45); }
.ba-score-team { display: flex; align-items: center; padding: 0 8px; color: #fff; font-weight: 700; font-size: var(--font-size-2xl); max-width: 110px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ba-score-team.is-home { border-radius: 4px 0 0 4px; }
.ba-score-team.is-away { border-radius: 0 4px 4px 0; }
.ba-score-pts { display: flex; align-items: center; justify-content: center; min-width: 34px; padding: 0 5px; background: #0b0b0b; color: #fff; font-weight: 800; font-size: var(--font-size-6xl); }
.ba-score-pts.is-pop { color: #e9e33b; animation: ba-pop 0.9s cubic-bezier(.2, 1.6, .4, 1) forwards; }
.ba-score-clock { display: flex; flex-direction: column; align-items: center; justify-content: center; min-width: 46px; padding: 0 5px; background: #1f2937; color: #e9e33b; font-size: var(--font-size-md); font-weight: 700; line-height: 1.05; }
.ba-score-clock b { color: #fff; font-size: var(--font-size-xs); font-weight: 600; opacity: 0.8; }
@keyframes ba-pop { 0% { transform: scale(0.4); } 40% { transform: scale(1.5); } 100% { transform: scale(1); color: #fff; } }

.ba-possession { position: absolute; left: 0; right: 0; top: 235px; display: flex; justify-content: center; align-items: center; gap: 7px; font-size: var(--font-size-3xl); z-index: 2; }
.ba-possession-label { color: #dbe7f1; font-weight: 600; }
.ba-possession-name { color: #fff; font-weight: 500; }
.ba-possession-bar { width: 2px; height: 15px; }

/* Basket overlay. */
.ba-point-overlay {
  position: absolute; inset: 0; z-index: 5; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 2px;
  background: radial-gradient(ellipse 70% 60% at 50% 50%, rgba(0, 0, 0, 0.35) 0%, rgba(0, 0, 0, 0.7) 100%);
  pointer-events: none;
}
.ba-point-overlay::before {
  content: ''; position: absolute; left: 0; right: 0; top: 50%; height: 110px; transform: translateY(-50%) skewY(-4deg);
  background: linear-gradient(90deg, transparent 0%, var(--side-color) 20%, var(--side-color) 80%, transparent 100%); opacity: 0.85;
  animation: ba-band 0.5s cubic-bezier(.2, .9, .3, 1) both;
}
.ba-point-value { position: relative; color: #e9e33b; font-size: var(--font-size-18xl); font-weight: 900; line-height: 1; text-shadow: 0 3px 12px rgba(0, 0, 0, 0.55); animation: ba-title 0.6s cubic-bezier(.2, 1.5, .4, 1) 0.05s both; }
.ba-point-title { position: relative; color: #fff; font-size: var(--font-size-10xl); font-weight: 900; font-style: italic; text-transform: uppercase; text-shadow: 0 3px 12px rgba(0, 0, 0, 0.55); animation: ba-title 0.6s cubic-bezier(.2, 1.5, .4, 1) 0.18s both; }
.ba-point-team { position: relative; color: #fff; font-size: var(--font-size-4xl); font-weight: 700; animation: ba-rise 0.4s ease-out 0.3s both; }
.ba-point-score { position: relative; display: flex; gap: 8px; margin-top: 4px; padding: 3px 12px; background: #0b0b0b; border-radius: 4px; color: #fff; font-size: var(--font-size-7xl); font-weight: 800; animation: ba-rise 0.4s ease-out 0.45s both; }
.ba-point-score .is-won { display: inline-block; color: #e9e33b; animation: ba-pop 0.9s cubic-bezier(.2, 1.6, .4, 1) 0.7s both; }
.ba-point-sep { opacity: 0.6; }
@keyframes ba-band { from { transform: translateY(-50%) skewY(-4deg) scaleX(0); } to { transform: translateY(-50%) skewY(-4deg) scaleX(1); } }
@keyframes ba-title { from { transform: scale(2.4); opacity: 0; } to { transform: scale(1); opacity: 1; } }
@keyframes ba-rise { from { transform: translateY(10px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
.ba-overlay-enter-active { transition: opacity 0.2s; }
.ba-overlay-leave-active { transition: opacity 0.4s; }
.ba-overlay-enter, .ba-overlay-leave-to { opacity: 0; }

/* Match over. */
/* Timeout card. */
.ba-card-veil { position: absolute; inset: 0; z-index: 4; display: flex; align-items: center; justify-content: center; background: rgba(0, 0, 0, 0.38); }
.ba-card {
  display: flex; flex-direction: column; align-items: center; gap: 2px;
  min-width: 170px; padding: 10px 26px 11px; border-radius: 6px;
  background: rgba(31, 41, 55, 0.95); border-bottom: 3px solid var(--side-color);
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.45);
}
.ba-card-icon { width: 42px; height: 42px; margin-bottom: 2px; animation: ba-card-icon 0.7s cubic-bezier(.2, 1.5, .4, 1) 0.1s both; }
.ba-card-hand { transform-box: view-box; transform-origin: 20px 23px; animation: ba-card-hand 2s linear infinite; }
.ba-card-title { font: italic 900 var(--font-size-9xl)/1.05 'Roboto', sans-serif; color: #fff; text-transform: uppercase; animation: ba-rise 0.4s ease-out 0.2s both; }
.ba-card-team { font: 700 var(--font-size-4xl)/1.2 'Roboto', sans-serif; color: #fff; max-width: 220px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; animation: ba-rise 0.4s ease-out 0.35s both; }
@keyframes ba-card-icon { from { transform: scale(0.3) rotate(-20deg); opacity: 0; } to { transform: scale(1) rotate(0); opacity: 1; } }
@keyframes ba-card-hand { from { transform: rotate(0); } to { transform: rotate(360deg); } }
.ba-card-enter-active { transition: opacity 0.25s ease; }
.ba-card-enter-active .ba-card { animation: ba-card-in 0.45s cubic-bezier(.2, 1.3, .4, 1) both; }
.ba-card-leave-active { transition: opacity 0.35s ease; }
.ba-card-enter, .ba-card-leave-to { opacity: 0; }
@keyframes ba-card-in { from { transform: scale(0.7); } to { transform: scale(1); } }

.ba-final { position: absolute; inset: 0; z-index: 6; display: flex; align-items: center; justify-content: center; background: rgba(5, 10, 16, 0.62); }
.ba-final-badge {
  display: flex; flex-direction: column; align-items: center; gap: 4px; padding: 14px 28px 16px; max-width: 420px;
  background: linear-gradient(160deg, #1f2937 0%, #0b1220 100%); border: 2px solid #facc15; border-radius: 14px;
  box-shadow: 0 0 0 4px rgba(250, 204, 21, 0.15), 0 12px 30px rgba(0, 0, 0, 0.6);
  animation: ba-final-in 0.7s cubic-bezier(.2, 1.4, .4, 1) both;
}
.ba-final-icon { width: 40px; height: 40px; animation: ba-trophy 2s ease-in-out 0.7s infinite; }
.ba-final-title { color: #facc15; font-size: var(--font-size-10xl); font-weight: 900; text-transform: uppercase; white-space: nowrap; }
.ba-final-score { display: flex; gap: 8px; color: #cbd5e1; font-size: var(--font-size-5xl); font-weight: 700; white-space: nowrap; }
.ba-final-score .is-win { color: #fff; }
.ba-final-sets { display: flex; gap: 10px; margin-top: 2px; color: #94a3b8; font-size: var(--font-size-2xl); font-weight: 700; }
@keyframes ba-final-in { from { transform: scale(0.3) rotate(-6deg); opacity: 0; } to { transform: scale(1) rotate(0); opacity: 1; } }
@keyframes ba-trophy { 0%, 100% { transform: translateY(0) rotate(0); } 25% { transform: translateY(-3px) rotate(-6deg); } 75% { transform: translateY(-3px) rotate(6deg); } }
.ba-final-enter-active { transition: opacity 0.3s; }
.ba-final-enter { opacity: 0; }
.arena-logo { opacity: 0.95; pointer-events: none; }
</style>
